import MuxPlayer, { type MuxPlayerRefAttributes } from "@mux/mux-player-react";
import type { Ref } from "react";
import { cn } from "../utils";

/**
 * The player a {@link VideoPlayer} `playerRef` resolves to. Re-exported so a caller that drives
 * playback needs this package and not the player library underneath it.
 */
export type VideoPlayerRef = MuxPlayerRefAttributes;

/** Props Interface */
interface VideoPlayerProps {
	playbackId: string;
	tokens: {
		playback?: string;
		thumbnail?: string;
		storyboard?: string;
	};
	aspectRatio?: string | null;
	className?: string;
	/** Overrides the poster; pass "" for a player whose token set carries no thumbnail token. */
	poster?: string;
	/**
	 * The underlying media element, for a caller that has to drive playback rather than only show
	 * it — a timeline that seeks to the frame a handle was dragged to, and reads back where the
	 * playhead stands. Everything else leaves it out and gets the player's own controls.
	 */
	playerRef?: Ref<MuxPlayerRefAttributes>;
}

/**
 * The playback surface for a Mux-hosted video. Every playback id this codebase creates
 * carries the `signed` policy, so the player is only ever handed a token set — a URL without
 * one answers 403 by design. The aspect ratio arrives as Mux stores it (`"16:9"`) and is
 * applied before the first frame, so the layout does not jump when the stream attaches.
 *
 * The width follows from the ratio against a capped height instead of filling the
 * container: a portrait recording on a wide screen would otherwise grow thousands of pixels
 * tall and push everything after it — a second video, most visibly — far below the fold.
 */
export function VideoPlayer({
	playbackId,
	tokens,
	aspectRatio,
	className,
	poster,
	playerRef,
}: VideoPlayerProps) {
	/** Computed */
	const [width, height] = (aspectRatio ?? "16:9").split(":").map(Number);
	const ratio = width && height ? width / height : 16 / 9;

	/** Render */
	return (
		<MuxPlayer
			ref={playerRef}
			playbackId={playbackId}
			tokens={tokens}
			streamType="on-demand"
			poster={poster}
			className={cn("overflow-hidden rounded-md", className)}
			style={{ aspectRatio: String(ratio), width: `min(100%, calc(32rem * ${ratio}))` }}
		/>
	);
}
